import {
  Baby,
  BadgeCheck,
  Bookmark,
  CalendarClock,
  ChevronDown,
  CircleDollarSign,
  Eye,
  Film,
  Globe,
  Images,
  MessageSquare,
  Languages,
  LayoutGrid,
  ListFilter,
  Pin,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Timer,
  TriangleAlert,
  Users
} from 'lucide-react';
import {useMemo, useState, type ReactNode} from 'react';
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Separator,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {useLanguageSearch, useLanguages, useLocations} from '@/api/hooks';
import {type ItemsQuery} from '@/api/items';
import {PLATFORM_ID} from '@/lib/platform';
import {FilterChip} from '@/components/shared/FilterChip';
import {
  MultiSelectFilterBody,
  type MultiSelectOption
} from '@/components/shared/MultiSelectFilterBody';
import {useDebounce} from '@/lib/useDebounce';
import {cn} from '@/lib/utils';
import {DEFAULT_ITEM_FILTERS, type ItemFilterState, type TriState} from '@/store/uiStore';

export function itemFilterQuery(filters: Omit<ItemFilterState, 'sortOrder'>): Partial<ItemsQuery> {
  const query: Partial<ItemsQuery> = {};
  const tri = (value: TriState) => value === 'true';
  if (filters.ageRestrictedFilter) {
    query.age_restricted = tri(filters.ageRestrictedFilter);
  }

  if (filters.monetizedFilter) {
    query.channel_monetized = tri(filters.monetizedFilter);
  }

  if (filters.verifiedFilter) {
    query.verified = tri(filters.verifiedFilter);
  }

  if (filters.madeForKidsFilter) {
    query.made_for_kids = tri(filters.madeForKidsFilter);
  }

  if (filters.isShortFilter) {
    query.is_short = tri(filters.isShortFilter);
  }

  if (filters.safeForMinorsFilter) {
    query.safe_for_minors = tri(filters.safeForMinorsFilter);
  }

  if (filters.aiGeneratedFilter) {
    query.ai_generated = tri(filters.aiGeneratedFilter);
  }

  if (filters.contentWarningFilter) {
    query.content_warning = tri(filters.contentWarningFilter);
  }

  if (filters.thumbnailChangedFilter) {
    query.thumbnail_changed = tri(filters.thumbnailChangedFilter);
  }

  if (filters.commentsAllowedFilter) {
    query.comments_allowed = tri(filters.commentsAllowedFilter);
  }

  if (filters.newOnlyFilter) {
    query.new_only = true;
  }

  const whole = (value: string) => Math.trunc(Number(value));
  if (filters.minSubscribersFilter) {
    query.min_subscribers = whole(filters.minSubscribersFilter);
  }

  if (filters.minViewsFilter) {
    query.min_views = whole(filters.minViewsFilter);
  }

  if (filters.maxSubscribersFilter) {
    query.max_subscribers = whole(filters.maxSubscribersFilter);
  }

  if (filters.maxViewsFilter) {
    query.max_views = whole(filters.maxViewsFilter);
  }

  if (filters.minPinsFilter) {
    query.min_pins = whole(filters.minPinsFilter);
  }

  if (filters.minBoardsFilter) {
    query.min_boards = whole(filters.minBoardsFilter);
  }

  if (filters.minSavesFilter) {
    query.min_saves = whole(filters.minSavesFilter);
  }

  if (filters.minDurationFilter) {
    query.min_duration_seconds = whole(filters.minDurationFilter) * 60;
  }

  if (filters.maxDurationFilter) {
    query.max_duration_seconds = whole(filters.maxDurationFilter) * 60;
  }

  if (filters.lastPostSinceFilter) {
    query.last_post_since = filters.lastPostSinceFilter;
  }

  if (filters.publishedSinceFilter) {
    query.published_since = filters.publishedSinceFilter;
  }

  if (filters.publishedUntilFilter) {
    query.published_until = filters.publishedUntilFilter;
  }

  if (filters.contentLanguageFilter.length > 0) {
    query.language = filters.contentLanguageFilter;
  }

  if (filters.contentCountryFilter.length > 0) {
    query.country = filters.contentCountryFilter;
  }

  if (filters.rdFilters.length > 0) {
    // Values are already canonical JSON; parsing/re-stringifying would round large numbers.
    const groups = filters.rdFilters.map(
      ({signal, values}) => `{"signal":${JSON.stringify(signal)},"values":[${values.join(',')}]}`
    );
    query.rd_filters = `[${groups.join(',')}]`;
  }

  return query;
}

const TRI_STATE_OPTIONS = [
  ['any', 'Any'],
  ['true', 'Yes'],
  ['false', 'No']
] as const;

function FlagFilterRow({
  label,
  icon: Icon,
  value,
  onChange
}: {
  readonly label: string;
  readonly icon: typeof ShieldAlert;
  readonly value: TriState;
  readonly onChange: (next: TriState) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-1.5 py-1">
      <span className="text-foreground flex items-center gap-1.5 text-xs font-medium">
        <Icon className="text-muted-foreground size-3.5" />
        {label}
      </span>
      <ToggleGroup
        variant="outline"
        spacing={0}
        size="sm"
        aria-label={label}
        value={[value || 'any']}
        onValueChange={(next) => {
          // A single-select group reports [] when the pressed item is clicked again - "Any" is
          // the resting state, so a deselect just stays where it is.
          const option = next[0];
          if (option === 'any') {
            onChange('');
          } else if (option === 'true' || option === 'false') {
            onChange(option);
          }
        }}>
        {TRI_STATE_OPTIONS.map(([option, optionLabel]) => (
          <ToggleGroupItem
            key={option}
            value={option}
            className={cn(
              'h-6',
              option === 'false' &&
                'aria-pressed:bg-destructive/10 aria-pressed:text-destructive-ink data-pressed:bg-destructive/10 data-pressed:text-destructive-ink'
            )}>
            {optionLabel}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

function NumberFilterRow({
  label,
  icon: Icon,
  value,
  onChange,
  type = 'number',
  placeholder
}: {
  readonly label: string;
  readonly icon: typeof ShieldAlert;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly type?: 'number' | 'date';
  readonly placeholder?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-1.5 py-1">
      <span className="text-foreground flex items-center gap-1.5 text-xs font-medium">
        <Icon className="text-muted-foreground size-3.5" />
        {label}
      </span>
      <Input
        type={type}
        aria-label={label}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 1 : undefined}
        inputMode={type === 'number' ? 'numeric' : undefined}
        placeholder={placeholder}
        value={value}
        className={cn('h-6 w-28 px-2', type === 'number' && 'text-right')}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onKeyDown={(e) => {
          // The dropdown menu intercepts keystrokes for its own arrow-key/typeahead navigation -
          // without this, typing into the input never reaches it.
          e.stopPropagation();
        }}
      />
    </div>
  );
}

type TriKey =
  | 'ageRestrictedFilter'
  | 'madeForKidsFilter'
  | 'isShortFilter'
  | 'safeForMinorsFilter'
  | 'aiGeneratedFilter'
  | 'contentWarningFilter'
  | 'thumbnailChangedFilter'
  | 'commentsAllowedFilter'
  | 'monetizedFilter'
  | 'verifiedFilter';

type TextKey =
  | 'minSubscribersFilter'
  | 'minViewsFilter'
  | 'maxSubscribersFilter'
  | 'maxViewsFilter'
  | 'minPinsFilter'
  | 'minSavesFilter'
  | 'minBoardsFilter'
  | 'minDurationFilter'
  | 'maxDurationFilter'
  | 'lastPostSinceFilter'
  | 'publishedSinceFilter'
  | 'publishedUntilFilter';

const YOUTUBE_ONLY = PLATFORM_ID !== 'pinterest';

const languageNames = new Intl.DisplayNames(['en'], {type: 'language', fallback: 'code'});

// A chip only holds the code, so it is named the way the browser names it rather than by a lookup
// in the language list, which would need the list loaded wherever a chip is shown.
function languageLabel(code: string) {
  try {
    return languageNames.of(code) ?? code;
  } catch {
    return code;
  }
}

const countryNames = new Intl.DisplayNames(['en'], {type: 'region', fallback: 'code'});

// Named by the browser, as languageLabel is, so a chip needs no locations list loaded.
function countryLabel(code: string) {
  try {
    return countryNames.of(code) ?? code;
  } catch {
    return code;
  }
}

// The value is the language's BCP-47 tag, which is what YouTube declares an item's language with.
function languageOption(item: {name: string; metadata: Record<string, unknown>}) {
  const tag = item.metadata.bcp47;
  return typeof tag === 'string' && tag !== '' ? {value: tag, label: item.name} : null;
}

export type ActiveItemFilter = {
  readonly id: string;
  readonly label: string;
  readonly patch: Partial<ItemFilterState>;
};

export function activeItemFilters(filters: ItemFilterState, showFreshness = true) {
  const active: ActiveItemFilter[] = [];
  const tri = (key: TriKey, label: string) => {
    const value = filters[key];
    if (value) {
      active.push({
        id: key,
        label: `${label}: ${value === 'true' ? 'Yes' : 'No'}`,
        patch: {[key]: ''}
      });
    }
  };

  const text = (key: TextKey, label: string) => {
    const value = filters[key];
    if (value) {
      active.push({id: key, label: `${label}: ${value}`, patch: {[key]: ''}});
    }
  };

  if (YOUTUBE_ONLY) {
    tri('ageRestrictedFilter', 'Age restricted');
    tri('madeForKidsFilter', 'Made for kids');
    tri('isShortFilter', 'Short');
    tri('safeForMinorsFilter', 'Safe for minors');
    tri('aiGeneratedFilter', 'AI generated');
    tri('contentWarningFilter', 'Content warning');
    tri('thumbnailChangedFilter', 'Thumbnail changed');
    tri('commentsAllowedFilter', 'Comments enabled');
    tri('monetizedFilter', 'Monetised');
    text('minDurationFilter', 'Min length (mins)');
    text('maxDurationFilter', 'Max length (mins)');
  }

  tri('verifiedFilter', 'Verified');
  text('minSubscribersFilter', YOUTUBE_ONLY ? 'Min subscribers' : 'Min followers');
  text('maxSubscribersFilter', YOUTUBE_ONLY ? 'Max subscribers' : 'Max followers');
  if (YOUTUBE_ONLY) {
    text('minViewsFilter', 'Min views');
    text('maxViewsFilter', 'Max views');
  } else {
    text('minPinsFilter', 'Min pins');
    text('minSavesFilter', 'Min saves');
    text('minBoardsFilter', 'Min boards');
  }

  text('lastPostSinceFilter', YOUTUBE_ONLY ? 'Last post since' : 'Last pin saved since');
  text('publishedSinceFilter', 'Created from');
  text('publishedUntilFilter', 'Created to');
  for (const code of filters.contentLanguageFilter) {
    active.push({
      id: `language-${code}`,
      label: `Language: ${languageLabel(code)}`,
      patch: {contentLanguageFilter: filters.contentLanguageFilter.filter((c) => c !== code)}
    });
  }

  for (const code of filters.contentCountryFilter) {
    active.push({
      id: `country-${code}`,
      label: `Country: ${countryLabel(code)}`,
      patch: {contentCountryFilter: filters.contentCountryFilter.filter((c) => c !== code)}
    });
  }

  if (showFreshness && filters.newOnlyFilter) {
    active.push({id: 'newOnlyFilter', label: 'New items only', patch: {newOnlyFilter: false}});
  }

  return active;
}

function LanguageFilterRow({
  value,
  onChange
}: {
  readonly value: string[];
  readonly onChange: (next: string[]) => void;
}) {
  // The default list is the ~184 default-visible languages; the rest of the ISO 639-3 set is
  // reached by searching, the same way the Submissions language filter does it.
  const {data: languages} = useLanguages();
  const [search, setSearch] = useState('');
  const term = useDebounce(search, 300).trim();
  const {data: searchResults} = useLanguageSearch(term);
  const options = useMemo<MultiSelectOption[]>(() => {
    const source =
      term.length >= 2
        ? (searchResults ?? [])
        : (languages ?? []).filter((item) => item.name.toLowerCase().includes(term.toLowerCase()));
    const found = source.flatMap((item) => languageOption(item) ?? []);
    // Keep a checked language visible while a search would otherwise hide it.
    const missing = value
      .filter((code) => !found.some((option) => option.value === code))
      .map((code) => ({value: code, label: languageLabel(code)}));
    return [...found, ...missing];
  }, [term, searchResults, languages, value]);
  const summary =
    value.length === 0
      ? 'Any'
      : value.length === 1
        ? languageLabel(value[0] ?? '')
        : `${value.length} selected`;

  return (
    <div className="flex items-center justify-between gap-3 px-1.5 py-1">
      <span className="text-foreground flex items-center gap-1.5 text-xs font-medium">
        <Languages className="text-muted-foreground size-3.5" />
        Language
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              aria-label="Language"
              className="h-6 w-28 justify-between px-2 text-xs font-normal"
            />
          }>
          <span className="truncate">{summary}</span>
          <ChevronDown className="text-muted-foreground size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 p-2">
          <MultiSelectFilterBody
            label="Languages"
            options={options}
            selected={value}
            emptyLabel="No languages match."
            onChange={onChange}
            onSearchChange={setSearch}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function CountryFilterRow({
  value,
  onChange
}: {
  readonly value: string[];
  readonly onChange: (next: string[]) => void;
}) {
  // The locations pack's countries (rows carrying attrs.countryCode; regions have none), as
  // CountrySelect lists them. ~250 rows, so filtered locally.
  const {data: locations} = useLocations();
  const [search, setSearch] = useState('');
  const options = useMemo<MultiSelectOption[]>(() => {
    const term = search.trim().toLowerCase();
    return (locations ?? [])
      .map((l) => ({value: String(l.metadata?.countryCode ?? ''), label: l.name}))
      .filter((o) => o.value && (value.includes(o.value) || o.label.toLowerCase().includes(term)))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [locations, search, value]);
  const summary =
    value.length === 0
      ? 'Any'
      : value.length === 1
        ? countryLabel(value[0] ?? '')
        : `${value.length} selected`;

  return (
    <div className="flex items-center justify-between gap-3 px-1.5 py-1">
      <span className="text-foreground flex items-center gap-1.5 text-xs font-medium">
        <Globe className="text-muted-foreground size-3.5" />
        Country
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="outline"
              aria-label="Country"
              className="h-6 w-28 justify-between px-2 text-xs font-normal"
            />
          }>
          <span className="truncate">{summary}</span>
          <ChevronDown className="text-muted-foreground size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 p-2">
          <MultiSelectFilterBody
            label="Countries"
            options={options}
            selected={value}
            emptyLabel="No countries match."
            onChange={onChange}
            onSearchChange={setSearch}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

type ItemFilterFieldsProps = {
  readonly filters: ItemFilterState;
  readonly onChange: (patch: Partial<ItemFilterState>) => void;
  readonly showFreshness?: boolean;
  readonly showPublished?: boolean;
  readonly showLastPostSince?: boolean;
  // Off where the fields sit in a dropdown menu: the picker is a menu of its own.
  readonly showLanguage?: boolean;
};

function FilterGroup({label, children}: {readonly label: string; readonly children: ReactNode}) {
  return (
    <div role="group" aria-label={label} className="space-y-0.5">
      <div className="text-muted-foreground px-1.5 py-1 text-xs font-medium">{label}</div>
      {children}
    </div>
  );
}

export function ItemFilterFields({
  filters,
  onChange,
  showFreshness = true,
  showPublished = true,
  showLastPostSince = true,
  showLanguage = false
}: ItemFilterFieldsProps) {
  const {
    ageRestrictedFilter,
    monetizedFilter,
    verifiedFilter,
    madeForKidsFilter,
    isShortFilter,
    safeForMinorsFilter,
    aiGeneratedFilter,
    contentWarningFilter,
    thumbnailChangedFilter,
    commentsAllowedFilter,
    newOnlyFilter,
    minSubscribersFilter,
    minViewsFilter,
    maxSubscribersFilter,
    maxViewsFilter,
    minPinsFilter,
    minBoardsFilter,
    minSavesFilter,
    minDurationFilter,
    maxDurationFilter,
    lastPostSinceFilter,
    publishedSinceFilter,
    publishedUntilFilter,
    contentLanguageFilter,
    contentCountryFilter
  } = filters;
  const youtubeOnly = YOUTUBE_ONLY;

  const createdRows = showPublished ? (
    <>
      <NumberFilterRow
        label="Created from"
        icon={CalendarClock}
        type="date"
        value={publishedSinceFilter}
        onChange={(next) => {
          onChange({publishedSinceFilter: next});
        }}
      />
      <NumberFilterRow
        label="Created to"
        icon={CalendarClock}
        type="date"
        value={publishedUntilFilter}
        onChange={(next) => {
          onChange({publishedUntilFilter: next});
        }}
      />
    </>
  ) : null;

  return (
    <div>
      {youtubeOnly ? (
        <>
          <FilterGroup label="Video">
            <FlagFilterRow
              label="Age restricted"
              icon={ShieldAlert}
              value={ageRestrictedFilter}
              onChange={(next) => {
                onChange({ageRestrictedFilter: next});
              }}
            />
            <FlagFilterRow
              label="Made for kids"
              icon={Baby}
              value={madeForKidsFilter}
              onChange={(next) => {
                onChange({madeForKidsFilter: next});
              }}
            />
            <FlagFilterRow
              label="Short"
              icon={Film}
              value={isShortFilter}
              onChange={(next) => {
                onChange({isShortFilter: next});
              }}
            />
            <FlagFilterRow
              label="Safe for minors"
              icon={ShieldCheck}
              value={safeForMinorsFilter}
              onChange={(next) => {
                onChange({safeForMinorsFilter: next});
              }}
            />
            <FlagFilterRow
              label="AI generated"
              icon={Sparkles}
              value={aiGeneratedFilter}
              onChange={(next) => {
                onChange({aiGeneratedFilter: next});
              }}
            />
            <FlagFilterRow
              label="Content warning"
              icon={TriangleAlert}
              value={contentWarningFilter}
              onChange={(next) => {
                onChange({contentWarningFilter: next});
              }}
            />
            <FlagFilterRow
              label="Thumbnail changed"
              icon={Images}
              value={thumbnailChangedFilter}
              onChange={(next) => {
                onChange({thumbnailChangedFilter: next});
              }}
            />
            <FlagFilterRow
              label="Comments enabled"
              icon={MessageSquare}
              value={commentsAllowedFilter}
              onChange={(next) => {
                onChange({commentsAllowedFilter: next});
              }}
            />
            <NumberFilterRow
              label="Min length (mins)"
              icon={Timer}
              value={minDurationFilter}
              placeholder="e.g. 1"
              onChange={(next) => {
                onChange({minDurationFilter: next});
              }}
            />
            <NumberFilterRow
              label="Max length (mins)"
              icon={Timer}
              value={maxDurationFilter}
              placeholder="e.g. 10"
              onChange={(next) => {
                onChange({maxDurationFilter: next});
              }}
            />
            {createdRows}
            {showLanguage ? (
              <>
                <LanguageFilterRow
                  value={contentLanguageFilter}
                  onChange={(next) => {
                    onChange({contentLanguageFilter: next});
                  }}
                />
                <CountryFilterRow
                  value={contentCountryFilter}
                  onChange={(next) => {
                    onChange({contentCountryFilter: next});
                  }}
                />
              </>
            ) : null}
          </FilterGroup>
          <Separator className="my-1.5" />
        </>
      ) : null}
      {!youtubeOnly && showPublished ? (
        <>
          <FilterGroup label="Pin">{createdRows}</FilterGroup>
          <Separator className="my-1.5" />
        </>
      ) : null}
      <FilterGroup label={youtubeOnly ? 'Channel' : 'Profile'}>
        {youtubeOnly ? (
          <FlagFilterRow
            label="Monetised"
            icon={CircleDollarSign}
            value={monetizedFilter}
            onChange={(next) => {
              onChange({monetizedFilter: next});
            }}
          />
        ) : null}
        <FlagFilterRow
          label="Verified"
          icon={BadgeCheck}
          value={verifiedFilter}
          onChange={(next) => {
            onChange({verifiedFilter: next});
          }}
        />
        <NumberFilterRow
          label={youtubeOnly ? 'Min subscribers' : 'Min followers'}
          icon={Users}
          value={minSubscribersFilter}
          placeholder="e.g. 1000"
          onChange={(next) => {
            onChange({minSubscribersFilter: next});
          }}
        />
        <NumberFilterRow
          label={youtubeOnly ? 'Max subscribers' : 'Max followers'}
          icon={Users}
          value={maxSubscribersFilter}
          placeholder="e.g. 5000"
          onChange={(next) => {
            onChange({maxSubscribersFilter: next});
          }}
        />
        {youtubeOnly ? (
          <>
            <NumberFilterRow
              label="Min views"
              icon={Eye}
              value={minViewsFilter}
              placeholder="e.g. 10000"
              onChange={(next) => {
                onChange({minViewsFilter: next});
              }}
            />
            <NumberFilterRow
              label="Max views"
              icon={Eye}
              value={maxViewsFilter}
              placeholder="e.g. 1000"
              onChange={(next) => {
                onChange({maxViewsFilter: next});
              }}
            />
          </>
        ) : (
          <>
            <NumberFilterRow
              label="Min pins"
              icon={Pin}
              value={minPinsFilter}
              placeholder="e.g. 100"
              onChange={(next) => {
                onChange({minPinsFilter: next});
              }}
            />
            <NumberFilterRow
              label="Min saves"
              icon={Bookmark}
              value={minSavesFilter}
              placeholder="e.g. 500"
              onChange={(next) => {
                onChange({minSavesFilter: next});
              }}
            />
            <NumberFilterRow
              label="Min boards"
              icon={LayoutGrid}
              value={minBoardsFilter}
              placeholder="e.g. 5"
              onChange={(next) => {
                onChange({minBoardsFilter: next});
              }}
            />
          </>
        )}
        {showLastPostSince ? (
          <NumberFilterRow
            label={youtubeOnly ? 'Last post since' : 'Last pin saved since'}
            icon={CalendarClock}
            type="date"
            value={lastPostSinceFilter}
            onChange={(next) => {
              onChange({lastPostSinceFilter: next});
            }}
          />
        ) : null}
      </FilterGroup>
      {showFreshness ? (
        <>
          <Separator className="my-1.5" />
          <FilterGroup label="Freshness">
            <label className="flex items-center gap-2 px-1.5 py-1 text-xs font-medium">
              <Checkbox
                checked={newOnlyFilter}
                onCheckedChange={(next) => {
                  onChange({newOnlyFilter: next});
                }}
              />
              <Sparkles className="text-muted-foreground size-3.5" />
              New items only
            </label>
          </FilterGroup>
        </>
      ) : null}
    </div>
  );
}

export type FiltersMenuProps = Omit<ItemFilterFieldsProps, 'onChange'> & {
  readonly onChange: (filters: ItemFilterState) => void;
};

export function FiltersMenu({filters, onChange, ...props}: FiltersMenuProps) {
  const activeCount = activeItemFilters(filters, props.showFreshness ?? true).length;
  const update = (patch: Partial<ItemFilterState>) => {
    onChange({...filters, ...patch});
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <FilterChip
            active={activeCount > 0}
            icon={<ListFilter />}
            label="Filters"
            count={activeCount}
          />
        }
      />
      <DropdownMenuContent align="start" className="w-72 p-2">
        <ItemFilterFields filters={filters} onChange={update} {...props} />
        {activeCount > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                onChange({...DEFAULT_ITEM_FILTERS, sortOrder: filters.sortOrder});
              }}>
              Clear filters
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
